import {
  Pencil, Plus, Rocket, X,
} from "lucide-react";
import {
  lazy, Suspense, useCallback, useEffect, useState,
} from "react";
import type React from "react";
import {
  Link, Navigate, useLocation, useParams,
} from "react-router-dom";

import {
  fetchDocument,
  updateDocument,
  type BlockNoteDocument,
} from "@/components/blocknote";
import { SegmentedControl } from "@/design-system";
import { COMMITMENT_BLOCK_TYPE } from "@/components/blocknote/CommitmentBlock";
import {
  normalizePlaybookBlocksForMaterialization,
} from "@/components/blocknote/playbookMaterialization";
import { playbookSchema } from "@/components/blocknote/playbookSchema";
import {
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";
import { CreateTagModal } from "@/features/commitments/CreateTagModal";
import { SearchableSelect } from "@/features/commitments/SearchableSelect";
import { useCreateTagMutation } from "@/features/commitments/hooks";

import {
  fetchCommitmentCategoryOptions,
  fetchCommitmentTagOptions,
  type PlaybookCommitmentCategoryOption,
  type PlaybookCommitmentTagOption,
} from "./api";
import { ApplyPlaybookModal } from "./ApplyPlaybookModal";
import { RecentPlaybookApplicationsModal } from "./RecentPlaybookApplicationsModal";
import {
  useDuplicatePlaybookMutation,
  usePlaybookDetailQuery,
  useRefreshPlaybookBlocksMutation,
  useUpdatePlaybookMutation,
} from "./hooks";
import type { PlaybookListRow } from "./types";


type Block = {
  id: string;
  type: string;
  props: Record<string, unknown>;
  children?: unknown[];
};

type SaveState = "idle" | "saving" | "saved" | "error";

type EditingBlockState = {
  mode: "edit";
  blockId: string;
  draft: CommitmentDraft;
} | {
  mode: "create";
  draft: CommitmentDraft;
} | null;

type CommitmentDraft = {
  title: string;
  description: string;
  default_priority: "low" | "medium" | "high";
  default_complexity: "1" | "2" | "3" | "5";
  category_slug: string;
  tag_ids: string[];
  due_anchor: "" | "apply_date" | "plan_year_start" | "plan_year_end" | "bor_date";
  due_offset_months: string;
  due_offset_days: string;
  is_regulatory: boolean;
  depends_on_block_uids: string[];
};

type CommitmentValidationErrors = Partial<Record<keyof CommitmentDraft, string>>;

const FALLBACK_CATEGORIES: PlaybookCommitmentCategoryOption[] = [
  { slug: "service", display_name: "Service" },
];


const PlaybookBlockNoteCanvas = lazy(() =>
  import("@/components/blocknote/PlaybookBlockNoteCanvas").then((m) => ({
    default: m.PlaybookBlockNoteCanvas,
  })),
);


export function PlaybookEditorAliasRedirect() {
  const { playbookId } = useParams<{ playbookId: string }>();
  return (
    <Navigate
      to={`/dashboard/playbooks/${playbookId ?? ""}/edit`}
      replace
    />
  );
}


export function PlaybookEditorPage() {
  const { playbookId = "" } = useParams<{ playbookId: string }>();
  const location = useLocation();
  const detail = usePlaybookDetailQuery(playbookId);

  if (detail.isLoading || detail.isPending) {
    return <DashboardLoadingState variant="card" />;
  }
  if (detail.isError || !detail.data) {
    return (
      <DashboardErrorState
        title="Couldn't load Playbook"
        description="Refresh to retry, or return to the Playbook Library."
        retry={() => detail.refetch()}
      />
    );
  }
  return (
    <PlaybookEditorBody
      playbook={detail.data}
      canonicalPath={`/dashboard/playbooks/${playbookId}/edit`}
      currentPath={location.pathname}
    />
  );
}


function PlaybookEditorBody({
  playbook,
  canonicalPath,
}: {
  playbook: PlaybookListRow;
  canonicalPath: string;
  currentPath: string;
}) {
  const [doc, setDoc] = useState<BlockNoteDocument | null>(null);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [loadingDoc, setLoadingDoc] = useState(true);
  const [canvasVersion, setCanvasVersion] = useState(0);
  const [categories, setCategories] =
    useState<PlaybookCommitmentCategoryOption[]>(FALLBACK_CATEGORIES);
  const [tags, setTags] = useState<PlaybookCommitmentTagOption[]>([]);
  const [editingBlock, setEditingBlock] = useState<EditingBlockState>(null);
  const [applyOpen, setApplyOpen] = useState(false);
  const [recentOpen, setRecentOpen] = useState(false);
  const [playbookName, setPlaybookName] = useState(playbook.name);
  const [titleDraft, setTitleDraft] = useState(playbook.name);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const refreshMutation = useRefreshPlaybookBlocksMutation();
  const duplicateMutation = useDuplicatePlaybookMutation();
  const updatePlaybookMutation = useUpdatePlaybookMutation();
  const readOnly = playbook.is_system;

  const openCreateCommitment = useCallback(() => {
    if (readOnly) return;
    setEditingBlock({ mode: "create", draft: blankDraft() });
  }, [readOnly]);

  useEffect(() => {
    let cancelled = false;
    setLoadingDoc(true);
    setLoadError(null);
    if (!playbook.blocknote_document_id) {
      setDoc(null);
      setBlocks([]);
      setLoadingDoc(false);
      return () => {
        cancelled = true;
      };
    }
    fetchDocument(playbook.blocknote_document_id)
      .then((d) => {
        if (cancelled) return;
        setDoc(d);
        setBlocks(readBlocks(d.blocks_json));
        setLoadingDoc(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          err instanceof Error ? err.message : "Couldn't load document.",
        );
        setLoadingDoc(false);
      });
    return () => {
      cancelled = true;
    };
  }, [playbook.blocknote_document_id]);

  useEffect(() => {
    if (readOnly) return;
    const ac = new AbortController();
    Promise.all([
      fetchCommitmentCategoryOptions({ signal: ac.signal }),
      fetchCommitmentTagOptions({ signal: ac.signal }),
    ])
      .then(([categoryResp, tagResp]) => {
        if (ac.signal.aborted) return;
        setCategories(
          categoryResp.categories.length > 0
            ? categoryResp.categories
            : FALLBACK_CATEGORIES,
        );
        setTags(tagResp.tags);
      })
      .catch(() => {
        if (ac.signal.aborted) return;
        setCategories(FALLBACK_CATEGORIES);
        setTags([]);
      });
    return () => ac.abort();
  }, [readOnly]);

  useEffect(() => {
    setPlaybookName(playbook.name);
    setTitleDraft(playbook.name);
  }, [playbook.name]);

  const persistBlocks = useCallback(async (nextBlocks: Block[]) => {
    if (readOnly || !doc) return;
    setSaveState("saving");
    setSaveError(null);
    const cleaned = removeStaleDependencies(nextBlocks);
    const normalized = normalizePlaybookBlocksForMaterialization(cleaned);
    const text = blocksToText(normalized.blocks);
    try {
      const updated = await updateDocument(doc.document_id, {
        blocks_json: normalized,
        blocks_text: text,
      });
      await refreshMutation.mutateAsync({
        playbookId: playbook.playbook_id,
        blocksJson: normalized,
      });
      setDoc(updated);
      setSaveState("saved");
    } catch (err: unknown) {
      setSaveState("error");
      setSaveError(
        err instanceof Error
          ? `Saved, but this Playbook is not ready to apply. ${err.message}`
          : "Saved, but this Playbook is not ready to apply.",
      );
    }
  }, [doc, playbook.playbook_id, readOnly, refreshMutation]);

  const commitBlocks = useCallback((
    nextBlocks: Block[],
    opts?: { remountCanvas?: boolean },
  ) => {
    if (readOnly) return;
    const cleaned = removeStaleDependencies(nextBlocks);
    setBlocks(cleaned);
    if (opts?.remountCanvas) {
      setCanvasVersion((version) => version + 1);
    }
    void persistBlocks(cleaned);
  }, [persistBlocks, readOnly]);

  const openBlockEditor = useCallback((block: Block) => {
    if (readOnly) return;
    setEditingBlock({
      mode: "edit",
      blockId: block.id,
      draft: draftFromBlock(block),
    });
  }, [readOnly]);

  const closeBlockEditor = useCallback(() => {
    setEditingBlock(null);
  }, []);

  const saveBlockEditor = useCallback((draft: CommitmentDraft) => {
    if (!editingBlock) return;
    if (editingBlock.mode === "create") {
      const block: Block = {
        id: crypto.randomUUID(),
        type: COMMITMENT_BLOCK_TYPE,
        props: propsFromDraft(draft),
        children: [],
      };
      commitBlocks([...blocks, block], { remountCanvas: true });
      setEditingBlock(null);
      return;
    }
    commitBlocks(blocks.map((block) =>
      block.id === editingBlock.blockId
        ? { ...block, props: { ...block.props, ...propsFromDraft(draft) } }
        : block,
    ), { remountCanvas: true });
    setEditingBlock(null);
  }, [blocks, commitBlocks, editingBlock]);

  const syncBlocksFromCanvas = useCallback((nextBlocks: unknown[]) => {
    if (readOnly) return;
    commitBlocks(readBlocks(nextBlocks));
  }, [commitBlocks, readOnly]);

  const duplicateBlock = useCallback((blockId: string) => {
    if (readOnly) return;
    const block = blocks.find((b) => b.id === blockId);
    if (!block) return;
    const copied: Block = {
      ...block,
      id: crypto.randomUUID(),
      props: { ...block.props },
      children: Array.isArray(block.children) ? [...block.children] : [],
    };
    const index = blocks.findIndex((b) => b.id === block.id);
    const next = index < 0
      ? [...blocks, copied]
      : [
          ...blocks.slice(0, index + 1),
          copied,
          ...blocks.slice(index + 1),
        ];
    commitBlocks(next, { remountCanvas: true });
  }, [blocks, commitBlocks, readOnly]);

  const deleteBlock = useCallback((blockId: string) => {
    if (readOnly) return;
    commitBlocks(blocks
      .filter((block) => block.id !== blockId)
      .map((block) => block.type === COMMITMENT_BLOCK_TYPE
        ? {
            ...block,
            props: {
              ...block.props,
              depends_on_block_uids: stringListValue(
                block.props.depends_on_block_uids,
              )
                .filter((id) => id !== blockId)
                .join(","),
            },
          }
        : block),
      { remountCanvas: true },
    );
  }, [blocks, commitBlocks, readOnly]);

  const openBlockEditorById = useCallback((blockId: string) => {
    const block = blocks.find((b) => b.id === blockId);
    if (block) openBlockEditor(block);
  }, [blocks, openBlockEditor]);

  const beginTitleEdit = useCallback(() => {
    if (readOnly) return;
    setTitleError(null);
    setTitleDraft(playbookName);
    setEditingTitle(true);
  }, [playbookName, readOnly]);

  const cancelTitleEdit = useCallback(() => {
    setTitleDraft(playbookName);
    setEditingTitle(false);
    setTitleError(null);
  }, [playbookName]);

  const submitTitleEdit = useCallback(async () => {
    if (readOnly) return;
    const nextName = titleDraft.trim();
    if (!nextName) {
      setTitleError("Playbook title is required.");
      return;
    }
    if (nextName === playbookName) {
      setEditingTitle(false);
      return;
    }
    setTitleError(null);
    try {
      const updated = await updatePlaybookMutation.mutateAsync({
        playbookId: playbook.playbook_id,
        name: nextName,
      });
      setPlaybookName(updated.name);
      setTitleDraft(updated.name);
      setEditingTitle(false);
    } catch (err: unknown) {
      setTitleError(
        err instanceof Error ? err.message : "Couldn't rename Playbook.",
      );
    }
  }, [
    playbook.playbook_id,
    playbookName,
    readOnly,
    titleDraft,
    updatePlaybookMutation,
  ]);

  async function duplicateSystemPlaybook() {
    const created = await duplicateMutation.mutateAsync(playbook.playbook_id);
    window.location.assign(`/dashboard/playbooks/${created.playbook_id}/edit`);
  }

  if (loadingDoc) {
    return <DashboardLoadingState variant="card" />;
  }
  if (loadError) {
    return (
      <DashboardErrorState
        title="Couldn't load editor document"
        description={loadError}
        retry={() => window.location.reload()}
      />
    );
  }

  return (
    <div
      data-testid="playbook-editor-page"
      className="flex flex-col gap-5"
    >
      <span
        data-testid="playbook-editor-canonical-path"
        className="hidden"
      >
        {canonicalPath}
      </span>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to="/dashboard/playbooks"
            className="text-sm text-teal hover:underline"
          >
            Back to Playbooks
          </Link>
          {editingTitle ? (
            <div className="mt-2">
              <input
                type="text"
                data-testid="playbook-title-input"
                value={titleDraft}
                autoFocus
                onChange={(e) => {
                  setTitleDraft(e.target.value);
                  setTitleError(null);
                }}
                onBlur={() => void submitTitleEdit()}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    void submitTitleEdit();
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    cancelTitleEdit();
                  }
                }}
                className="w-full min-w-[16rem] rounded-md border border-border-subtle bg-canvas px-2 py-1 text-lg font-semibold tracking-tight text-text-primary"
              />
              {titleError && (
                <p role="alert" className="mt-1 text-xs text-ember-ink">
                  {titleError}
                </p>
              )}
            </div>
          ) : (
            <h2 className="mt-2 flex items-center gap-2 text-lg font-semibold tracking-tight text-text-primary">
              <span>{playbookName}</span>
              {!readOnly && (
                <button
                  type="button"
                  data-testid="playbook-title-edit"
                  aria-label="Edit Playbook title"
                  onClick={beginTitleEdit}
                  className="rounded-md p-1 text-text-tertiary hover:bg-recessed hover:text-text-primary"
                >
                  <Pencil size={14} aria-hidden="true" />
                </button>
              )}
            </h2>
          )}
          {playbook.description && (
            <p className="mt-1 max-w-2xl text-sm text-text-secondary">
              {playbook.description}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {readOnly && (
            <button
              type="button"
              data-testid="playbook-editor-duplicate-cta"
              onClick={() => void duplicateSystemPlaybook()}
              className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
            >
              Duplicate to edit
            </button>
          )}
          <button
            type="button"
            data-testid="playbook-editor-recent-applications"
            onClick={() => setRecentOpen(true)}
            className="inline-flex items-center gap-1 rounded-md border border-border-subtle bg-canvas px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-recessed"
          >
            Recent applications
          </button>
          <button
            type="button"
            data-testid="playbook-editor-apply"
            onClick={() => setApplyOpen(true)}
            className="inline-flex items-center gap-1 rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            <Rocket size={15} aria-hidden="true" />
            Apply Playbook
          </button>
        </div>
      </header>

      {readOnly && (
        <div
          data-testid="playbook-editor-readonly"
          className="rounded-md border border-border-subtle bg-recessed px-3 py-2 text-sm text-text-secondary"
        >
          System Playbooks are read-only. Duplicate this Playbook to customize it.
        </div>
      )}

      <div
        data-testid="playbook-blocknote-schema-mounted"
        data-schema-blocks={Object.keys(playbookSchema.blockSchema).join(",")}
        className="rounded-lg border border-border-subtle bg-raised p-4"
      >
        <div className="mb-3 text-xs uppercase tracking-wide text-text-tertiary">
          Playbook document
        </div>
        <Suspense
          fallback={
            <div className="rounded-md border border-border-subtle bg-recessed px-3 py-4 text-sm text-text-tertiary">
              Loading Playbook editor...
            </div>
          }
        >
          <PlaybookBlockNoteCanvas
            key={canvasVersion}
            blocks={blocks}
            readOnly={readOnly}
            onBlocksChange={syncBlocksFromCanvas}
            onInsertCommitment={openCreateCommitment}
            categories={categories}
            tags={tags}
            onEditCommitment={openBlockEditorById}
            onDuplicateCommitment={duplicateBlock}
            onDeleteCommitment={deleteBlock}
          />
        </Suspense>
        {blocks.length === 0 && (
          <div className="mt-4 rounded-md border border-dashed border-border-subtle px-3 py-6 text-center text-sm text-text-tertiary">
            Add a Commitment block to start this Playbook.
          </div>
        )}
      </div>

      {editingBlock && !readOnly && (
        <CommitmentEditModal
          key={editingBlock.mode === "edit" ? editingBlock.blockId : "create"}
          mode={editingBlock.mode}
          initialDraft={editingBlock.draft}
          currentBlockId={
            editingBlock.mode === "edit" ? editingBlock.blockId : null
          }
          dependencyBlocks={blocks.filter((block) =>
            block.type === COMMITMENT_BLOCK_TYPE,
          )}
          categories={categories}
          tags={tags}
          onTagCreated={(tag) => {
            setTags((prev) =>
              prev.some((existing) => existing.id === tag.id)
                ? prev
                : [...prev, tag],
            );
          }}
          onCancel={closeBlockEditor}
          onSave={saveBlockEditor}
        />
      )}

      {saveState === "saving" && (
        <p className="text-xs text-text-tertiary">Saving...</p>
      )}
      {saveState === "saved" && (
        <p className="text-xs text-teal">Saved and materialized.</p>
      )}
      {saveError && (
        <p role="alert" className="text-sm text-ember-ink">{saveError}</p>
      )}
      <ApplyPlaybookModal
        playbook={applyOpen ? { ...playbook, name: playbookName } : null}
        onClose={() => setApplyOpen(false)}
      />
      <RecentPlaybookApplicationsModal
        playbookId={playbook.playbook_id}
        playbookName={playbookName}
        open={recentOpen}
        onClose={() => setRecentOpen(false)}
      />
    </div>
  );
}


function CommitmentEditModal({
  mode,
  initialDraft,
  currentBlockId,
  dependencyBlocks,
  categories,
  tags,
  onTagCreated,
  onCancel,
  onSave,
}: {
  mode: "create" | "edit";
  initialDraft: CommitmentDraft;
  currentBlockId: string | null;
  dependencyBlocks: Block[];
  categories: PlaybookCommitmentCategoryOption[];
  tags: PlaybookCommitmentTagOption[];
  onTagCreated: (tag: PlaybookCommitmentTagOption) => void;
  onCancel: () => void;
  onSave: (draft: CommitmentDraft) => void;
}) {
  const [draft, setDraft] = useState<CommitmentDraft>(initialDraft);
  const [errors, setErrors] = useState<CommitmentValidationErrors>({});
  const [createTagOpen, setCreateTagOpen] = useState(false);
  const createTagMutation = useCreateTagMutation();
  const categorySlugs = categories.map((category) => category.slug);
  const titleOnly = mode === "create" && draft.title.trim().length === 0;
  const selectedTagLabels = tags
    .filter((tag) => draft.tag_ids.includes(tag.id) || draft.tag_ids.includes(tag.slug))
    .map((tag) => tag.display_name);
  const dependencyOptions = dependencyBlocks
    .filter((block) => block.id !== currentBlockId)
    .map((block) => ({
      value: block.id,
      label: stringValue(block.props.title) || "Untitled Commitment",
    }));
  const selectedDependencyLabels = dependencyOptions
    .filter((opt) => draft.depends_on_block_uids.includes(opt.value))
    .map((opt) => opt.label);
  const update = (field: keyof CommitmentDraft, value: string | boolean | string[]) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };
  const submit = () => {
    const nextErrors = validateDraft(draft, categorySlugs);
    if (
      currentBlockId &&
      draft.depends_on_block_uids.includes(currentBlockId)
    ) {
      nextErrors.depends_on_block_uids =
        "A Commitment cannot depend on itself.";
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSave(draft);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/35 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="commitment-edit-title"
        className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-border-subtle bg-canvas shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2
            id="commitment-edit-title"
            className="text-base font-semibold text-text-primary"
          >
            {mode === "create" ? "Create Commitment" : "Edit Commitment"}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onCancel}
            className="rounded-md p-1 text-text-secondary hover:bg-recessed"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
          <TextInput
            label="Title"
            value={draft.title}
            error={errors.title}
            required
            onChange={(value) => update("title", value)}
          />
          {titleOnly && (
            <div
              data-testid="playbook-commitment-create-title-step"
              className="rounded-md border border-border-subtle bg-recessed px-3 py-2 text-sm text-text-secondary"
            >
              Name the Commitment to continue.
            </div>
          )}
          {!titleOnly && (
            <>
          <TextArea
            label="Description"
            value={draft.description}
            onChange={(value) => update("description", value)}
          />
          <div
            data-testid="playbook-commitment-priority-complexity-row"
            className="grid gap-4 md:grid-cols-2"
          >
            <SegmentedControl
              label="Priority"
              value={draft.default_priority}
              options={[
                { value: "low", label: "Low" },
                { value: "medium", label: "Medium" },
                { value: "high", label: "High" },
              ]}
              onChange={(value) =>
                update("default_priority", value as CommitmentDraft["default_priority"])}
            />
            <SegmentedControl
              label="Complexity"
              value={draft.default_complexity}
              options={[
                { value: "1", label: "S" },
                { value: "2", label: "M" },
                { value: "3", label: "L" },
                { value: "5", label: "XL" },
              ]}
              onChange={(value) =>
                update("default_complexity", value as CommitmentDraft["default_complexity"])}
            />
          </div>
          <SelectInput
            label="Category"
            value={draft.category_slug}
            error={errors.category_slug}
            options={categories.map((category) => ({
              value: category.slug,
              label: category.display_name,
            }))}
            onChange={(value) => update("category_slug", value)}
          />
          <TagPicker
            tags={tags}
            selectedIds={draft.tag_ids}
            selectedLabels={selectedTagLabels}
            onChange={(ids) => update("tag_ids", ids)}
            onCreateTag={() => setCreateTagOpen(true)}
          />
          <SelectInput
            label="Due date anchor"
            value={draft.due_anchor}
            error={errors.due_anchor}
            options={[
              { value: "", label: "None" },
              { value: "apply_date", label: "Apply date" },
              { value: "plan_year_start", label: "Plan year start" },
              { value: "plan_year_end", label: "Plan year end" },
              { value: "bor_date", label: "Broker of Record date" },
            ]}
            onChange={(value) =>
              update("due_anchor", value as CommitmentDraft["due_anchor"])}
          />
          <div className="grid gap-3 md:grid-cols-2">
            <TextInput
              label="Offset months"
              type="number"
              min={0}
              value={draft.due_offset_months}
              error={errors.due_offset_months}
              onChange={(value) => update("due_offset_months", value)}
            />
            <TextInput
              label="Offset days"
              type="number"
              min={0}
              value={draft.due_offset_days}
              error={errors.due_offset_days}
              onChange={(value) => update("due_offset_days", value)}
            />
          </div>
          <p className="text-sm font-medium text-text-primary">
            {dueRuleSummary(
              draft.due_anchor,
              draft.due_offset_months,
              draft.due_offset_days,
            )}
          </p>
          <FieldShell label="Dependencies">
            <SearchableSelect
              fieldId="playbook-commitment-dependencies"
              multiple
              value={draft.depends_on_block_uids}
              options={dependencyOptions}
              triggerDisplay={
                selectedDependencyLabels.length === 0
                  ? <span className="italic text-text-tertiary">No dependencies</span>
                  : (
                    <span className="flex flex-wrap gap-1">
                      {selectedDependencyLabels.map((label) => (
                        <span
                          key={label}
                          className="rounded-full bg-recessed px-2 py-0.5 text-[11px] text-text-secondary"
                        >
                          {label}
                        </span>
                      ))}
                    </span>
                  )
              }
              onPick={(blockId) => {
                if (blockId === currentBlockId) return;
                update("depends_on_block_uids", [
                  ...draft.depends_on_block_uids,
                  blockId,
                ]);
              }}
              onUnpick={(blockId) => {
                update(
                  "depends_on_block_uids",
                  draft.depends_on_block_uids.filter((id) => id !== blockId),
                );
              }}
            />
            {errors.depends_on_block_uids && (
              <span className="text-xs text-ember-ink">
                {errors.depends_on_block_uids}
              </span>
            )}
          </FieldShell>
            </>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-border-subtle px-5 py-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-primary hover:bg-recessed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            className="rounded-md bg-teal px-3 py-1.5 text-sm font-medium text-text-inverse hover:opacity-90"
          >
            Save Commitment
          </button>
        </footer>
      </section>
      <CreateTagModal
        open={createTagOpen}
        isPending={createTagMutation.isPending}
        error={
          createTagMutation.isError
            ? (createTagMutation.error as Error).message
            : null
        }
        onCancel={() => {
          setCreateTagOpen(false);
          createTagMutation.reset();
        }}
        onCreate={({ display_name, is_regulatory }) => {
          createTagMutation.mutate(
            { display_name, is_regulatory },
            {
              onSuccess: (tag) => {
                onTagCreated(tag);
                update("tag_ids", [...draft.tag_ids, tag.id]);
                setCreateTagOpen(false);
                createTagMutation.reset();
              },
            },
          );
        }}
      />
    </div>
  );
}


function TextInput({
  label,
  value,
  onChange,
  error,
  type = "text",
  min,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  min?: number;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm text-text-primary">
      <span>
        {label}{required ? <span className="text-ember-ink"> *</span> : null}
      </span>
      <input
        aria-label={label}
        type={type}
        min={min}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border-subtle bg-canvas px-3 py-2 text-sm text-text-primary"
      />
      {error && <span className="text-xs text-ember-ink">{error}</span>}
    </label>
  );
}


function TextArea({
  label, value, onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm text-text-primary">
      {label}
      <textarea
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="rounded-md border border-border-subtle bg-canvas px-3 py-2 text-sm text-text-primary"
      />
    </label>
  );
}


function SelectInput({
  label, value, options, onChange, error,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  error?: string;
}) {
  return (
    <label className="flex flex-col gap-1 text-sm text-text-primary">
      {label}
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-border-subtle bg-canvas px-3 py-2 text-sm text-text-primary"
      >
        {options.map((option) => (
          <option key={option.value || "none"} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && <span className="text-xs text-ember-ink">{error}</span>}
    </label>
  );
}


function TagPicker({
  tags, selectedIds, selectedLabels, onChange, onCreateTag,
}: {
  tags: PlaybookCommitmentTagOption[];
  selectedIds: string[];
  selectedLabels: string[];
  onChange: (ids: string[]) => void;
  onCreateTag: () => void;
}) {
  return (
    <FieldShell label="Tags">
      <div className="flex flex-col gap-1">
        <SearchableSelect
          fieldId="playbook-commitment-tags"
          multiple
          value={selectedIds}
          options={tags.map((tag) => ({
            value: tag.id,
            label: tag.display_name,
            hint: tag.is_regulatory ? "Regulatory" : undefined,
          }))}
          triggerDisplay={
            selectedLabels.length === 0
              ? <span className="italic text-text-tertiary">Add tags</span>
              : (
                <span className="flex flex-wrap gap-1">
                  {selectedLabels.slice(0, 4).map((label) => (
                    <span
                      key={label}
                      className="rounded-full bg-recessed px-2 py-0.5 text-[11px] text-text-secondary"
                    >
                      {label}
                    </span>
                  ))}
                  {selectedLabels.length > 4 && (
                    <span className="text-[11px] text-text-tertiary">
                      +{selectedLabels.length - 4}
                    </span>
                  )}
                </span>
              )
          }
          onPick={(tagId) => {
            if (!selectedIds.includes(tagId)) onChange([...selectedIds, tagId]);
          }}
          onUnpick={(tagId) => {
            onChange(selectedIds.filter((id) => id !== tagId));
          }}
        />
        <button
          type="button"
          data-testid="playbook-commitment-create-tag"
          onClick={onCreateTag}
          className="mt-1 inline-flex w-fit items-center gap-1 text-[11px] font-medium text-teal hover:underline"
        >
          <Plus size={10} aria-hidden="true" />
          Create tag
        </button>
      </div>
    </FieldShell>
  );
}


function FieldShell({
  label, children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1 text-sm text-text-primary">
      <span>{label}</span>
      {children}
    </div>
  );
}


function validateDraft(
  draft: CommitmentDraft,
  categorySlugs: string[],
): CommitmentValidationErrors {
  const errors: CommitmentValidationErrors = {};
  if (draft.title.trim().length === 0) {
    errors.title = "Title is required.";
  }
  if (draft.category_slug && !categorySlugs.includes(draft.category_slug)) {
    errors.category_slug = "Pick a valid category.";
  }
  if (!["low", "medium", "high"].includes(draft.default_priority)) {
    errors.default_priority = "Pick a valid priority.";
  }
  if (!["1", "2", "3", "5"].includes(draft.default_complexity)) {
    errors.default_complexity = "Pick a valid complexity.";
  }
  if (Number(draft.due_offset_months || 0) < 0) {
    errors.due_offset_months = "Months must be zero or greater.";
  }
  if (Number(draft.due_offset_days || 0) < 0) {
    errors.due_offset_days = "Days must be zero or greater.";
  }
  if (
    (!Number.isInteger(Number(draft.due_offset_months || 0))) ||
    (!Number.isInteger(Number(draft.due_offset_days || 0)))
  ) {
    if (!Number.isInteger(Number(draft.due_offset_months || 0))) {
      errors.due_offset_months = "Months must be a whole number.";
    }
    if (!Number.isInteger(Number(draft.due_offset_days || 0))) {
      errors.due_offset_days = "Days must be a whole number.";
    }
  }
  if (
    !draft.due_anchor &&
    (Number(draft.due_offset_months || 0) > 0 ||
      Number(draft.due_offset_days || 0) > 0)
  ) {
    errors.due_anchor = "Pick a due date anchor when offsets are set.";
  }
  if (
    draft.depends_on_block_uids.some((id) => !id || id === "")
  ) {
    errors.depends_on_block_uids = "Pick valid dependencies.";
  }
  return errors;
}


function blankDraft(): CommitmentDraft {
  return {
    title: "",
    description: "",
    default_priority: "low",
    default_complexity: "1",
    category_slug: "service",
    tag_ids: [],
    due_anchor: "",
    due_offset_months: "0",
    due_offset_days: "0",
    is_regulatory: false,
    depends_on_block_uids: [],
  };
}


function draftFromBlock(block: Block): CommitmentDraft {
  const p = block.props;
  return {
    title: stringValue(p.title),
    description: stringValue(p.description),
    default_priority: priorityValue(p.default_priority),
    default_complexity: complexityValue(p.default_complexity),
    category_slug: stringValue(p.category_slug) || "service",
    tag_ids: stringListValue(p.tag_ids),
    due_anchor: dueAnchorValue(p.due_anchor),
    due_offset_months: nonNegativeString(p.due_offset_months),
    due_offset_days: nonNegativeString(p.due_offset_days),
    is_regulatory: false,
    depends_on_block_uids: stringListValue(p.depends_on_block_uids),
  };
}


function propsFromDraft(draft: CommitmentDraft): Record<string, unknown> {
  return {
    title: draft.title.trim(),
    description: draft.description.trim(),
    default_priority: draft.default_priority,
    default_complexity: Number(draft.default_complexity),
    category_slug: draft.category_slug,
    tag_ids: draft.tag_ids.join(","),
    assigned_job_title: "",
    due_anchor: draft.due_anchor,
    due_offset_months: Number(draft.due_offset_months || 0),
    due_offset_days: Number(draft.due_offset_days || 0),
    depends_on_block_uids: draft.depends_on_block_uids.join(","),
    is_regulatory: false,
  };
}


function dueRuleSummary(
  anchor: CommitmentDraft["due_anchor"],
  monthsRaw: string,
  daysRaw: string,
): string {
  if (!anchor) return "No due date anchor set.";
  const label = dueAnchorLabel(anchor);
  const months = Number(monthsRaw || 0);
  const days = Number(daysRaw || 0);
  if (!months && !days) return `Due on ${label}.`;
  if (months && !days) {
    return `Due ${months} ${plural(months, "month")} after ${label}.`;
  }
  if (!months && days) {
    return `Due ${days} ${plural(days, "day")} after ${label}.`;
  }
  return `Due ${months} ${plural(months, "month")} and ${days} ${plural(days, "day")} after ${label}.`;
}


function dueAnchorLabel(anchor: CommitmentDraft["due_anchor"]): string {
  if (anchor === "plan_year_start") return "Plan year start";
  if (anchor === "plan_year_end") return "Plan year end";
  if (anchor === "bor_date") return "Broker of Record date";
  return "Apply date";
}


function plural(n: number, word: string): string {
  return n === 1 ? word : `${word}s`;
}


function priorityValue(value: unknown): CommitmentDraft["default_priority"] {
  return value === "high" || value === "medium" || value === "low"
    ? value
    : "low";
}


function complexityValue(value: unknown): CommitmentDraft["default_complexity"] {
  const s = String(value ?? "1");
  return s === "2" || s === "3" || s === "5" ? s : "1";
}


function dueAnchorValue(value: unknown): CommitmentDraft["due_anchor"] {
  if (
    value === "apply_date" ||
    value === "plan_year_start" ||
    value === "plan_year_end" ||
    value === "bor_date"
  ) {
    return value;
  }
  return "";
}


function nonNegativeString(value: unknown): string {
  if (value === null || value === undefined || value === "") return "0";
  return String(value);
}


function stringListValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    return value.split(",").map((v) => v.trim()).filter(Boolean);
  }
  return [];
}


function readBlocks(raw: unknown): Block[] {
  const blocks = Array.isArray(raw)
    ? raw
    : raw && typeof raw === "object"
    ? (raw as { blocks?: unknown }).blocks
    : raw;
  if (!Array.isArray(blocks)) return [];
  return blocks
    .filter((b): b is Record<string, unknown> =>
      Boolean(b && typeof b === "object"),
    )
    .map((b) => {
      const type = String(b.type || "paragraph");
      const props = (
        b.props && typeof b.props === "object"
          ? { ...(b.props as Record<string, unknown>) }
          : {}
      );
      if (type === COMMITMENT_BLOCK_TYPE) {
        props.assigned_job_title = "";
      }
      return {
        ...b,
        id: String(b.id || crypto.randomUUID()),
        type,
        props,
        children: Array.isArray(b.children) ? b.children : [],
      };
    });
}


function removeStaleDependencies(blocks: Block[]): Block[] {
  const validIds = new Set(
    blocks
      .filter((block) => block.type === COMMITMENT_BLOCK_TYPE)
      .map((block) => block.id),
  );
  return blocks.map((block) => {
    if (block.type !== COMMITMENT_BLOCK_TYPE) return block;
    const deps = stringListValue(block.props.depends_on_block_uids)
      .filter((id) => validIds.has(id) && id !== block.id);
    return {
      ...block,
      props: {
        ...block.props,
        depends_on_block_uids: deps.join(","),
      },
    };
  });
}


function stringValue(value: unknown): string {
  if (Array.isArray(value)) return value.join(", ");
  if (value === null || value === undefined) return "";
  return String(value);
}


function blocksToText(blocks: unknown[]): string {
  return blocks
    .map((block) => {
      if (!block || typeof block !== "object") return "";
      const props = (block as { props?: Record<string, unknown> }).props ?? {};
      return [props.title, props.description]
        .map((v) => typeof v === "string" ? v.trim() : "")
        .filter(Boolean)
        .join("\n");
    })
    .filter(Boolean)
    .join("\n");
}
